// Build a personalised greeting for an incoming visitor based on their
// IP-resolved location. Falls back gracefully if geo is unknown.

import { GeoInfo } from './Tracker';

export interface Greeting {
  primary: string;        // headline shown big
  subtitle?: string;      // smaller line below
  emoji?: string;
}

// Country-specific greetings — keyed by ISO 3166-1 alpha-2 code.
const COUNTRY_GREETINGS: Record<string, Greeting> = {
  // The personal ones first
  LB: { primary: 'مرحبا من قدامى الأرز',   subtitle: 'Welcome from the old cedars.',     emoji: '🇱🇧' },
  JP: { primary: 'こんにちは、日本から',     subtitle: 'Hello, from Japan.',                emoji: '🇯🇵' },
  UG: { primary: 'Webale kuja',              subtitle: 'Thank you for coming.',             emoji: '🇺🇬' },
  TZ: { primary: 'Karibu sana',              subtitle: 'You are very welcome.',             emoji: '🇹🇿' },
  FR: { primary: 'Bienvenue !',              subtitle: 'Vous venez de France.',             emoji: '🇫🇷' },

  // Common Francophone / strategic
  BE: { primary: 'Bienvenue de Belgique',                                                    emoji: '🇧🇪' },
  CH: { primary: 'Bienvenue de Suisse',                                                      emoji: '🇨🇭' },
  CA: { primary: 'Bienvenue / Welcome',      subtitle: 'From Canada.',                      emoji: '🇨🇦' },

  // Anglophone
  US: { primary: 'Howdy from the terminal', subtitle: 'Welcome from the US.',                emoji: '🇺🇸' },
  GB: { primary: 'Cheers, mate',             subtitle: 'Welcome from the UK.',              emoji: '🇬🇧' },
  AU: { primary: 'G\'day',                   subtitle: 'Welcome from Australia.',           emoji: '🇦🇺' },

  // Other languages — small touches
  DE: { primary: 'Willkommen!',              emoji: '🇩🇪' },
  IT: { primary: 'Benvenuti!',               emoji: '🇮🇹' },
  ES: { primary: 'Bienvenidos!',             emoji: '🇪🇸' },
  PT: { primary: 'Bem-vindo!',               emoji: '🇵🇹' },
  BR: { primary: 'Bem-vindo!',               subtitle: 'From Brazil.',                      emoji: '🇧🇷' },
  NL: { primary: 'Welkom!',                  emoji: '🇳🇱' },
  RU: { primary: 'Добро пожаловать',         emoji: '🇷🇺' },
  CN: { primary: '欢迎',                      emoji: '🇨🇳' },
  KR: { primary: '환영합니다',                 emoji: '🇰🇷' },
  TW: { primary: '歡迎',                      emoji: '🇹🇼' },
  IN: { primary: 'नमस्ते',                    emoji: '🇮🇳' },
  EG: { primary: 'أهلا وسهلا',                emoji: '🇪🇬' },
  MA: { primary: 'مرحبا',                     emoji: '🇲🇦' },
  SA: { primary: 'أهلا',                      emoji: '🇸🇦' },
  IL: { primary: 'ברוכים הבאים',              emoji: '🇮🇱' },
  TR: { primary: 'Hoş geldiniz',             emoji: '🇹🇷' },
  GR: { primary: 'Καλώς ήρθατε',              emoji: '🇬🇷' },
  PL: { primary: 'Witamy',                   emoji: '🇵🇱' },
  SE: { primary: 'Välkommen',                emoji: '🇸🇪' },
  NO: { primary: 'Velkommen',                emoji: '🇳🇴' },
  FI: { primary: 'Tervetuloa',               emoji: '🇫🇮' },
  KE: { primary: 'Karibu',                   emoji: '🇰🇪' },
  ET: { primary: 'እንኳን ደህና መጣህ',           emoji: '🇪🇹' },
  ZA: { primary: 'Welkom / Welcome',         emoji: '🇿🇦' },
  MX: { primary: 'Bienvenidos desde México',  emoji: '🇲🇽' },
  AR: { primary: 'Bienvenidos de Argentina', emoji: '🇦🇷' },
};

// City-specific overrides — keyed by lower-case city name.
// These take priority over country greetings when matched.
const CITY_GREETINGS: Record<string, Greeting> = {
  'beirut':     { primary: 'مرحبا يا بيروت',           subtitle: 'Hello, Beirut.',           emoji: '🇱🇧' },
  'tokyo':      { primary: '東京からようこそ',           subtitle: 'Welcome from Tokyo.',      emoji: '🇯🇵' },
  'osaka':      { primary: '大阪からようこそ',           subtitle: 'Welcome from Osaka.',      emoji: '🇯🇵' },
  'okayama':    { primary: '岡山からの訪問者',           subtitle: 'A visitor from Okayama — neighbour.', emoji: '🇯🇵' },
  'kurashiki':  { primary: '倉敷からこんにちは',         subtitle: 'Hello from Kurashiki.',    emoji: '🇯🇵' },
  'kampala':    { primary: 'Webale kuja, Kampala',     subtitle: 'Welcome from Kampala.',    emoji: '🇺🇬' },
  'paris':      { primary: 'Salutations parisiennes',  emoji: '🇫🇷' },
  'lyon':       { primary: 'Bienvenue de Lyon',        emoji: '🇫🇷' },
  'marseille':  { primary: 'Bienvenue de Marseille',   emoji: '🇫🇷' },
  'london':     { primary: 'Hello from across the channel',  emoji: '🇬🇧' },
  'new york':   { primary: 'Howdy from the Big Apple', emoji: '🇺🇸' },
  'san francisco': { primary: 'Greetings from the Bay', emoji: '🇺🇸' },
};

export function buildGreeting(geo: GeoInfo): Greeting | null {
  if (!geo || !geo.country_code) return null;

  // City has priority
  if (geo.city) {
    const cityKey = geo.city.toLowerCase();
    if (CITY_GREETINGS[cityKey]) return CITY_GREETINGS[cityKey];
  }

  if (geo.country_code && COUNTRY_GREETINGS[geo.country_code]) {
    const g = { ...COUNTRY_GREETINGS[geo.country_code] };
    if (geo.city) {
      g.subtitle = (g.subtitle ? g.subtitle + ' ' : '') + `(${geo.city})`;
    }
    return g;
  }

  // Fallback — generic with city/country if available
  if (geo.country) {
    return {
      primary: 'Welcome, visitor.',
      subtitle: geo.city ? `${geo.city}, ${geo.country}` : geo.country,
      emoji: '🌍',
    };
  }
  return null;
}
